import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { LinkUnclaimedBagDialog } from './LinkUnclaimedBagDialog'
import { DateWithUTCTooltip } from '../../common'
import { API_HOST, authenticatedFetch, CHARITY_APP_URL } from '../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { PaginationControls } from './PaginationControls'
import { BagdropReportButton } from './BagdropReportButton'
import { Link } from 'react-router-dom'
import { Button } from '@tomra/design-system-react/button'
import { Card } from '@tomra/design-system-react/card'
import { hasViewCharitiesPermission } from '../../../services/permission.service'

export const BagsPage = () => {
  const [lastPath, setLastPath] = useState<string>('')
  const [searchParams, setSearchParams] = useSearchParams()
  const [bagsResponse, setBagsResponse] = useState<BagsResponse>()
  const [findUnclaimedBagsStatus, setFindUnclaimedBagsStatus] = useState<RequestStatusType>('pending')
  const [error, setError] = useState<string>('')
  const [bagToLink, setBagToLink] = useState<UnlinkedBagType>()
  const [pageSize, setPageSize] = useState(() => {
    const pageSizeParam = searchParams.get('pageSize')
    return pageSizeParam ? parseInt(pageSizeParam, 10) : 20
  })
  const [next, setNext] = useState<{
    token: string | undefined
    page: number
  }>({
    token: undefined,
    page: 1
  })

  const getDefaultFromDate = () => new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1))
  const getDefaultToDate = () => new Date()

  const [reportFromDate, setReportFromDate] = useState(() => {
    const fromParam = searchParams.get('from')
    return fromParam ? new Date(fromParam) : getDefaultFromDate()
  })
  const [reportToDate, setReportToDate] = useState(() => {
    const toParam = searchParams.get('to')
    return toParam ? new Date(toParam) : getDefaultToDate()
  })
  const [reportAppContext, setReportAppContext] = useState<CharityAppContext>(() => {
    const appContextParam = searchParams.get('appContext') as CharityAppContext
    return appContextParam || 'RETURNANDEARN'
  })
  const [reportState, setReportState] = useState<string>(() => {
    const stateParam = searchParams.get('state')
    return stateParam || 'UNLINKED'
  })

  useEffect(() => {
    const newSearchParams = new URLSearchParams()
    newSearchParams.set('from', reportFromDate.toISOString().substring(0, 10))
    newSearchParams.set('to', reportToDate.toISOString().substring(0, 10))
    newSearchParams.set('appContext', reportAppContext)
    newSearchParams.set('state', reportState)
    newSearchParams.set('pageSize', pageSize.toString())
    setSearchParams(newSearchParams, { replace: true })

    setNext({ token: undefined, page: 1 })
  }, [reportFromDate, reportToDate, reportAppContext, reportState, pageSize])

  useEffect(() => {
    let params = `${reportState.toLowerCase()}?appContext=${reportAppContext}&from=${reportFromDate.toISOString().substring(0, 10)}T00:00:00.00Z&to=${reportToDate.toISOString().substring(0, 10)}T23:59:59.99Z&limit=${pageSize}`
    if (next.token) {
      params = `${reportState.toLowerCase()}?` + next.token.split('?')[1]
    }
    if (lastPath === params) {
      return
    }
    setLastPath(params)

    setFindUnclaimedBagsStatus('pending')
    setError('')
    setBagsResponse(undefined)

    const f = authenticatedFetch(`${API_HOST}/mytomra/v1.0/hotlinetool/bags/${params}`)
    f.run().then(
      (unclaimedBags: BagsResponse) => {
        setBagsResponse(unclaimedBags)
        setFindUnclaimedBagsStatus('success')
      },
      (error: any) => {
        setFindUnclaimedBagsStatus('failure')
        logError(new Error('Failed to fetch bags'), error)
        setError(error.body?.detail || 'Whoopsie! Failed to load bags. Please try again.')
      }
    )
  }, [next])

  const goBack = () => setNext(prev => ({ ...prev, page: prev.page - 1, token: bagsResponse?.meta.links?.prev }))
  const goNext = () => setNext(prev => ({ ...prev, page: prev.page + 1, token: bagsResponse?.meta.links?.next }))

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
  }

  if (findUnclaimedBagsStatus === 'pending' && bagsResponse?.data.length === 0) {
    return (
      <div className="centerAbsolute flex flex-col items-center space-y-4">
        <div className="loadingSpinner" />
        <p>This may take a while...</p>
      </div>
    )
  }

  const isPendingOrProccessed = reportState === 'PENDING' || reportState === 'PROCESSED'
  const isUnclaimedOrProccessed = reportState === 'UNLINKED' || reportState === 'PROCESSED'

  return (
    <div className="p-6">
      <h1 className="text-xl mb-4">Bags</h1>
      <Card.Root>
        <div className="">
          <BagdropReportButton
            fromDate={reportFromDate}
            toDate={reportToDate}
            appContext={reportAppContext}
            state={reportState}
            onFromDateChange={setReportFromDate}
            onToDateChange={setReportToDate}
            onAppContextChange={setReportAppContext}
            onStateChange={setReportState}
            recordCount={bagsResponse?.meta.totalItems ?? null}
          />
        </div>

        {findUnclaimedBagsStatus === 'pending' && (
          <div className="flex justify-center items-center p-4">
            <div className="loadingSpinner" />
          </div>
        )}

        {findUnclaimedBagsStatus === 'failure' && error && (
          <div className="my-4 relative mt-16">
            <h1 className="sr-only">Bags</h1>
            <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute">
              {error}
            </div>
          </div>
        )}

        {bagsResponse?.data && bagsResponse?.data.length > 0 && (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th>Bagtag</th>
                  {isUnclaimedOrProccessed && <th>Processed</th>}
                  {isUnclaimedOrProccessed && <th>Store Reference</th>}
                  {isPendingOrProccessed && <th>Scanned</th>}
                  {isPendingOrProccessed && <th>Delivered Location</th>}

                  {isPendingOrProccessed && <th>User ID</th>}
                  {isPendingOrProccessed && <th>Charity ID</th>}

                  {isUnclaimedOrProccessed && <th>Serial</th>}
                  {isUnclaimedOrProccessed && <th>Amount</th>}
                  {reportState == 'UNLINKED' && <th className="!text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {bagsResponse?.data.map(bag => {
                  return (
                    <tr key={bag.bagTag + bag}>
                      <td>{bag.bagTag}</td>
                      {isUnclaimedOrProccessed && (
                        <td>{bag.processedAt && <DateWithUTCTooltip date={new Date(bag.processedAt)} />}</td>
                      )}
                      {isUnclaimedOrProccessed && <td>{bag.storeReference}</td>}
                      {isPendingOrProccessed && (
                        <td>{bag.scannedAt && <DateWithUTCTooltip date={new Date(bag.scannedAt)} />}</td>
                      )}
                      {isPendingOrProccessed && <td>{bag.deliveredLocation}</td>}
                      {isPendingOrProccessed && (
                        <td>
                          {bag.linkedType === 'USER' ? (
                            <Link className="text-cyan-700 underline dark:text-cyan-400" to={'/user/' + bag.linkedTo}>
                              {bag.linkedTo}
                            </Link>
                          ) : (
                            '---'
                          )}
                        </td>
                      )}
                      {isPendingOrProccessed && (
                        <td>
                          {bag.linkedType === 'CHARITY' ? (
                            hasViewCharitiesPermission() ? (
                              <a
                                className="text-cyan-700 underline dark:text-cyan-400"
                                href={CHARITY_APP_URL + '/charities/' + bag.linkedTo}
                              >
                                {bag.linkedTo}
                              </a>
                            ) : (
                              bag.linkedTo
                            )
                          ) : (
                            '---'
                          )}
                        </td>
                      )}

                      {isUnclaimedOrProccessed && <td>{bag.serialNumber}</td>}
                      {isUnclaimedOrProccessed && <td>{bag.amount ? bag.amount.toFixed(2) + ' AUD' : ''}</td>}
                      {reportState == 'UNLINKED' && (
                        <td className="!text-right">
                          <Button onClick={() => setBagToLink(bag as UnlinkedBagType)}>Link bag to user</Button>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <PaginationControls
              findUnclaimedBagsStatus={findUnclaimedBagsStatus}
              goBack={goBack}
              goNext={goNext}
              goToFirst={() => setNext({ token: undefined, page: 1 })}
              currentPage={next.page}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              totalItems={bagsResponse?.meta.totalItems ?? 0}
            />
          </>
        )}
        {bagsResponse?.data && bagsResponse?.data.length === 0 && findUnclaimedBagsStatus !== 'failure' && (
          <div className="p-6 text-center text-gray-600">
            <p>No bags found for the selected filters.</p>
          </div>
        )}
      </Card.Root>

      {bagToLink && (
        <LinkUnclaimedBagDialog
          bagToLink={bagToLink}
          bagLinkedSuccessFully={() => setBagToLink(undefined)}
          closeDialog={() => setBagToLink(undefined)}
        />
      )}
    </div>
  )
}
